import { Button } from "@/components/ui/button"
import { ProjectStatusMenu } from "../../components/project-status-menu"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useProjectDetailContext } from "./project-detail-context"

function PencilIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	)
}

function ArchiveIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<rect x="3" y="4" width="18" height="4" rx="1" />
			<path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
			<path d="M10 12h4" />
		</svg>
	)
}

/**
 * Project identity + actions: type label, status menu, name, accepted cost,
 * Edit/Archive and the archived notice. Rendered by the Details tab ONLY —
 * the other sub-tabs show just their own content (the project name stays
 * reachable in the mobile top-bar breadcrumb; on desktop the rail marks
 * the current project section).
 */
export function ProjectHeader() {
	const {
		project: p,
		isOwner,
		canSetStatus,
		canEdit,
		canArchive,
		archived,
		editing,
		setEditing,
		onUpdateStatus,
		updateStatusPending,
		onSetArchived,
		setArchivedPending,
		runAction,
	} = useProjectDetailContext()
	const { locale } = useLocale()
	const t = getTranslations(locale)

	const paid = p.paidAt !== null
	const acceptedCost = p.acceptedCreationCost ?? null

	return (
		<header>
			<div className="flex items-start justify-between gap-4">
				<div className="min-w-0">
					<div className="flex items-center gap-3 flex-wrap">
						<span className="text-xs uppercase tracking-wide text-muted-foreground/80">
							{t.projects.types[p.type]}
						</span>
						<ProjectStatusMenu
							status={p.status}
							canSetStatus={canSetStatus}
							isOwner={isOwner}
							pending={updateStatusPending}
							onSelect={(s) => runAction(onUpdateStatus(s))}
						/>
					</div>
					<h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight mt-2 break-words">
						{p.name}
					</h1>
					{acceptedCost ? (
						<p className="mt-2 text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
							<span>
								{t.projects.cost.acceptedLabel}:{" "}
								<span className="text-foreground/80 font-semibold">
									{acceptedCost.amount} {acceptedCost.currency}
								</span>
							</span>
							{paid ? (
								<span className="inline-flex items-center rounded-sm border border-current/25 px-2 py-0.5 text-xs font-medium bg-success-muted text-success-muted-foreground">
									{t.projects.cost.paidBadge}
								</span>
							) : null}
						</p>
					) : null}
				</div>
				<div className="flex items-center gap-1 shrink-0">
					{canEdit && !editing && !archived ? (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setEditing(true)}
							aria-label={t.projects.detail.editCta}
						>
							<PencilIcon className="md:hidden" />
							<span className="hidden md:inline">
								{t.projects.detail.editCta}
							</span>
						</Button>
					) : null}
					{canArchive ? (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => runAction(onSetArchived(!archived))}
							disabled={setArchivedPending}
							aria-label={
								archived
									? t.projects.detail.unarchiveCta
									: t.projects.detail.archiveCta
							}
						>
							<ArchiveIcon className="md:hidden" />
							<span className="hidden md:inline">
								{archived
									? t.projects.detail.unarchiveCta
									: t.projects.detail.archiveCta}
							</span>
						</Button>
					) : null}
				</div>
			</div>

			{archived ? (
				<div className="mt-4 rounded-xl border border-border bg-accent text-foreground/80 text-sm px-4 py-3">
					{t.projects.detail.archivedNotice}
				</div>
			) : null}
		</header>
	)
}
